import { SocialPlatform } from '../types';

/**
 * The team's weekly posting rhythm, and the reasoning behind each slot.
 *
 * IMPORTANT — what this is and is not:
 *
 * These are **documented heuristics**, not measurements. They come from well-established
 * patterns in how each network's audience behaves, not from an analysis of this team's own
 * results. The page says so plainly, and it should keep saying so: presenting a rule of
 * thumb as though it were measured data is how a schedule ossifies for three years without
 * anyone questioning it, which is exactly what happened to the version this replaces.
 *
 * The right way to use it: post to this schedule for a month, then compare against
 * Analytics and move the slots that your own audience disagrees with.
 *
 * Everything here is editorial — days, times and themes are all judgement calls. Change them
 * freely; no layout code depends on the specific values.
 *
 * Times are local to the team (Massachusetts, US Eastern). A slot's `time` is 24-hour.
 */

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const WEEKDAYS: { key: Weekday; label: string; short: string }[] = [
  { key: 'mon', label: 'Monday', short: 'Mon' },
  { key: 'tue', label: 'Tuesday', short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday', short: 'Thu' },
  { key: 'fri', label: 'Friday', short: 'Fri' },
  { key: 'sat', label: 'Saturday', short: 'Sat' },
  { key: 'sun', label: 'Sunday', short: 'Sun' },
];

export interface Slot {
  /** What goes out — the editorial theme, carried over from the previous schedule. */
  theme: string;
  /** 24-hour local time, e.g. '08:30'. */
  time: string;
  /** Optional one-line note shown on hover. */
  note?: string;
  /**
   * Not every week. Employee spotlights happen when there is someone to feature, so the slot
   * is a standing reservation rather than a weekly obligation — shown dashed in the matrix so
   * an empty week does not read as a missed post.
   */
  occasional?: boolean;
  /**
   * A second post the same day. Exists because Monday carries both the employee spotlight
   * (occasional, whenever there is someone to feature) and a standing weekly theme — the
   * spotlight was originally given Monday outright, which quietly displaced the theme that
   * was already there.
   */
  also?: Omit<Slot, 'also'>;
}

/** A slot per weekday, per platform. Missing days are intentionally empty. */
export type PlatformSchedule = Partial<Record<Weekday, Slot>>;

/**
 * What changed from the previous schedule, and why. Kept in the file so the next person to
 * revisit this can see the reasoning rather than guessing at it.
 *
 * The old schedule put almost everything at 10:00–10:30 on Mon/Wed/Fri, with Instagram at
 * 13:00. Three things pushed the slots around:
 *
 *  1. **Mornings moved earlier for B2B** — but not as early as the 2026-08 revision assumed.
 *     That revision reasoned hybrid work had pushed professional feeds before 9am and put
 *     LinkedIn's blog post at 08:00 and its video at 08:30. The 2026-09-24 sources put the
 *     window at 9am–12pm instead, so both moved back into it. The direction was right and the
 *     magnitude was not, which is the difference between a recalled heuristic and a cited one.
 *  2. **Tue–Thu beats Mon/Wed/Fri.** Monday competes with inbox triage and Friday afternoon
 *     is dead for anything B2B. The mid-week trio consistently does better.
 *  3. **Instagram has three peaks, not one.** The old schedule used a single 1pm slot. There
 *     are actually three daily windows — 7–9am early scrollers, 11am–1pm lunch, and 5–9pm
 *     through commute and winding down — so the slots are spread across all three rather than
 *     clustered. The evening is strongest, especially for short video.
 */
export const SCHEDULE_CHANGELOG = {
  revised: '2026-10-10',
  previous: 'Circa 2023 — Mon/Wed/Fri at 10:00, Instagram at 13:00',
  summary:
    'Weekday slots moved earlier and onto Tue–Thu for the professional networks; Instagram ' +
    'spread across its three daily peaks; Saturday added for Instagram only. ' +
    '2026-08-22: X gained the employee spotlight on Monday now that images work there, and ' +
    'its repost moved to Friday to make room. ' +
    '2026-08-25: restored the team\'s own Monday Quote Day and the "Solar" in Solar Tips & ' +
    'Facts — the 2026-08-22 revision had given Monday to the employee spotlight outright, ' +
    'displacing a post that had run for years, and had genericised the Wednesday theme. Both ' +
    'now run on Monday at different peaks. Instagram rests Tuesday again. ' +
    '2026-09-24: five slot times moved into the 9am–1pm window the current aggregate sources ' +
    'agree on — LinkedIn blog 08:00→09:00 and video 08:30→09:30, Facebook engagement and ' +
    'video 13:00→12:00, X engagement 12:00→09:00. Days deliberately unchanged, and Instagram ' +
    'left on the team\'s own research. ' +
    '2026-10-10: every slot moved to the times the team has actually been posting at since ' +
    'early October, from ChatGPT recommendations — LinkedIn 11:30am, Facebook noon, X ' +
    '12:30pm, Instagram 3pm — with ChatGPT\'s windows behind the composer warnings. Days and ' +
    'themes unchanged. ' +
    '2026-10-17: the team\'s original days restored on LinkedIn, Facebook and X — Monday ' +
    'reshare, Tuesday Engagement Day, Wednesday Blog Post, Friday Video. The 2026-08-22 ' +
    'revision had swapped Tuesday and Wednesday and moved Video to Thursday, which was never ' +
    'asked for. The employee spotlight stays on Monday as an occasional second post.',
  // Instagram's three windows (7–9am, 11am–1pm, 5–9pm) came from research the team supplied
  // on 2026-08-20, not from the general heuristics behind the other platforms.
  instagramSource: 'the same, replacing the team\'s own 2026-08-20 research (peaks at 7–9am, 11am–1pm and 5–9pm)',

  // LinkedIn, Facebook and X moved on 2026-09-24 onto published-aggregator windows — Buffer,
  // Neil Patel and Research.com, by way of a Gemini summary. Better provenance than the
  // recalled heuristics they replace, and still not a measurement of THIS audience: they
  // describe social media in general, not SolBid's followers.
  professionalSource: 'ChatGPT recommendations the team posts to, 2026-10',

  // Those sources also proposed Wed–Fri 9am–1pm for Instagram, which is NOT applied.
  //
  // The team's own research is more specific and disagrees: all week, with peaks at 7–9am,
  // 11am–1pm and 5–9pm. Adopting the generic window would have dropped Monday Quote Day,
  // the Thursday evening Reels slot and Saturday altogether. Kept deliberately, and recorded
  // here so the disagreement is visible rather than silently resolved.
  instagramDissent:
    'Superseded 2026-10-10. The team\'s own 2026-08-20 research put Instagram at three peaks ' +
    '(7–9am, 11am–1pm, 5–9pm); the team now posts to ChatGPT\'s 1–7pm window instead, and the ' +
    'schedule follows what is actually being done. Recorded so the change is visible.',

  // 2026-10-10: the team had been posting at ChatGPT's suggested times for weeks while this
  // file still recommended 9–10am, so the composer warned about nearly every real post. The
  // schedule now records what is actually done. ChatGPT's windows came from one table Ian
  // supplied (best Tuesday window per network, plus his pick), applied to every posting day.
  // Still not a measurement of this audience — Stats is, once there is enough of it.
  //
  // Only the TIMES moved. (2026-10-17: the days are now the team's original ones again.)
  // Day assignments stay as the team set them: the Monday employee
  // spotlight, the lighter Friday reshare, Saturday on Instagram. The sources have no view on
  // an editorial calendar, and those choices were made for reasons they do not know about.
  daysUnchanged: true,
};

export const SCHEDULE: Partial<Record<SocialPlatform, PlatformSchedule>> = {
  // One time per network, all week: the times the team has actually been posting at since
  // early October (ChatGPT's suggestions). A consistent slot is easier to keep to than a
  // different hour each day, and nothing here measured a difference between days.
  // Days are the team's own, restored 2026-10-17 at Ian's choice: Monday reshare, Tuesday
  // Engagement Day, Wednesday Blog Post, Friday Video. The spotlight rides on Monday as an
  // occasional second post, as far from the reshare as each network's same-day rule asks.
  linkedin: {
    mon: {
      theme: 'Reshare',
      time: '11:30',
      note: 'Lighter touch to start the week',
      also: {
        theme: 'Employee spotlight',
        time: '15:30',
        note: 'When there is someone to feature — four hours after the reshare, or skip the reshare that week',
        occasional: true,
      },
    },
    tue: { theme: 'Engagement', time: '11:30', note: 'Comment and reply rather than broadcast' },
    wed: { theme: 'Blog post', time: '11:30', note: 'Late morning, inside the 11am–5pm window' },
    fri: { theme: 'Video', time: '11:30', note: 'Native video, captions burned in; before the afternoon tails off' },
  },
  facebook: {
    mon: {
      theme: 'Reshare',
      time: '12:00',
      also: {
        theme: 'Employee spotlight',
        time: '18:00',
        note: 'Cross-post of the LinkedIn spotlight, when there is one — six hours after the reshare',
        occasional: true,
      },
    },
    tue: { theme: 'Engagement', time: '12:00', note: 'Midday, while people are on a lunch break' },
    wed: { theme: 'Blog post', time: '12:00', note: 'Noon, the start of the 12–8pm window' },
    fri: { theme: 'Video', time: '12:00', note: 'Video holds attention through the afternoon' },
  },
  instagram: {
    mon: {
      theme: 'Employee spotlight',
      time: '15:00',
      note: 'Faces do well here — mid-window, when there is someone to feature',
      occasional: true,
      // Quote Day has run on Monday for years. Two posts the same day go two hours apart,
      // both inside the 1–7pm window, rather than stacked into one slot.
      also: {
        theme: 'Quote Day',
        time: '13:00',
        note: 'The long-standing Monday quote post, at the start of the 1–7pm window',
      },
    },
    wed: { theme: 'Solar Tips & Facts', time: '15:00', note: 'Mid-afternoon, inside the 1–7pm window' },
    thu: { theme: 'Reels / video', time: '15:00', note: 'Reels reach beyond followers; give them the afternoon' },
    sat: { theme: 'Behind the scenes', time: '15:00', note: 'Weekend works on Instagram and nowhere else' },
  },
  twitter: {
    // Employee spotlight sits on Monday with the other networks, not on a spare day: one
    // piece of content should go out across platforms the same day, and a spotlight landing
    // four days later reads as an afterthought.
    mon: {
      theme: 'Repost / commentary',
      time: '12:30',
      also: {
        theme: 'Employee spotlight',
        time: '14:30',
        note: 'When there is someone to feature — 280 characters, so keep it to a line',
        occasional: true,
      },
    },
    tue: { theme: 'Engagement', time: '12:30', note: 'Reply-heavy; stay around to answer' },
    wed: { theme: 'Blog post', time: '12:30' },
    fri: { theme: 'Video', time: '12:30' },
  },
};

export interface PlatformGuidance {
  /** Headline window, shown on the collapsed card. */
  headline: string;
  bestDays: string;
  bestTimes: string;
  secondary: string;
  avoid: string;
  cadence: string;
  /**
   * Posting twice in one day: whether it is worth doing here, and how far apart.
   *
   * Separate from `cadence`, which is a weekly count. The schedule already puts two themes on
   * one day (Monday's spotlight and reshare), so the question comes up in practice and the
   * answer differs sharply per network — daily is fine on X and actively harmful on LinkedIn.
   */
  sameDay: string;
  /** Why these windows — the reasoning, not just the numbers. */
  why: string;
  /** Format notes specific to this network. */
  format: string[];
}

/**
 * Posting more than once in a day — the rules that are not per-network.
 *
 * Added 2026-09-28 at Ian's request. The per-platform `sameDay` notes cover "should I post
 * twice here"; this covers the cases that span networks, which is most of what the team
 * actually does — the same post going to four places at once.
 */
export const SPACING_GUIDANCE: { title: string; body: string }[] = [
  {
    title: 'The same post to several networks at once is fine',
    body:
      'Audiences barely overlap, and nobody is following you on four networks and reading all ' +
      'four. Simultaneous publishing is what this app is for. The spacing rules below are ' +
      'about posting twice to the SAME network, which is a different question.',
  },
  {
    title: 'Two posts to one network need real distance',
    body:
      'Four hours on LinkedIn, six on Facebook, a couple on X, and on Instagram prefer not to ' +
      'at all. Closer than that and the second post competes with the first for the same ' +
      'audience in the same session rather than reaching anyone new.',
  },
  {
    title: 'Put the more important post first',
    body:
      'Where a network shows one post per author to most of an audience — LinkedIn most ' +
      'obviously — the second post suppresses the first rather than the other way round. If ' +
      'one of the two matters more, it goes in the earlier window.',
  },
  {
    title: 'Use different windows, not the same one twice',
    body:
      'Two posts inside the same peak reach the same people twice. A morning post and an ' +
      'evening one reach different halves of an audience, which is the whole point of ' +
      'posting twice.',
  },
  {
    title: 'A reply or a comment is not a second post',
    body:
      'Answering people under an existing post keeps it circulating and costs nothing in ' +
      'reach. If there is more to say within a few hours of publishing, say it in the ' +
      'thread rather than in a new post.',
  },
];

export const GUIDANCE: Partial<Record<SocialPlatform, PlatformGuidance>> = {
  linkedin: {
    headline: 'Weekdays, 11am–5pm',
    bestDays: 'Tuesday, Wednesday, Thursday',
    bestTimes: '11:00am–5:00pm — the team posts at 11:30am',
    secondary: 'Earlier mornings, 9–11am, still work for anything time-sensitive',
    avoid: 'Weekends entirely, and late Friday — B2B attention collapses',
    cadence: '3–5 posts per week. Posting daily on LinkedIn suppresses reach rather than adding it.',
    sameDay:
      'Avoid it. A second post the same day competes with the first rather than adding to it ' +
      '— most of an audience sees one post per author in a session, so the later one largely ' +
      'cannibalises the earlier. If a day genuinely needs two, leave at least four hours and ' +
      'put the lower-priority one in the afternoon.',
    why:
      'ChatGPT puts the LinkedIn window at 11am–5pm: late enough that the morning inbox ' +
      'rush is over and people are browsing rather than triaging, and finished before the ' +
      'working day ends. The team settled on 11:30am, early in the window, so a post has ' +
      'the afternoon to gather the comments that decide how far it travels.',
    format: [
      'Text plus a single image outperforms link posts — the algorithm demotes outbound links',
      'Put the link in the first comment instead of the post body',
      'Native video only; captions burned in, since most viewing is muted',
      'First two lines are all that show before "see more" — front-load the hook',
      'Documents and carousels hold attention longer than a static image',
      'Images: 1080 × 1350 (4:5) takes the most room in the feed; 1200 × 627 for a landscape image',
    ],
  },
  facebook: {
    headline: 'Weekdays, 12–8pm',
    bestDays: 'Tuesday through Friday',
    bestTimes: '12:00–8:00pm — the team posts at noon',
    secondary: 'Mornings before noon are acceptable, just quieter',
    avoid: 'Before 8am, and late Sunday',
    cadence: '3–5 posts per week for a Page. Organic Page reach is low; frequency does not rescue it.',
    sameDay:
      'Two is the practical ceiling, and only when the second earns its place. Space them by ' +
      'at least six hours — a morning post and an evening one reach different people, while ' +
      'two an hour apart split the same audience in half.',
    why:
      'ChatGPT puts the Facebook window at noon to 8pm: the audience is mixed rather than ' +
      'purely professional, so it is busiest from lunch through the evening. Page reach is ' +
      'mostly paid now, so what timing still buys is the first hour of engagement that ' +
      'decides whether a post is shown further — noon gives it the whole window.',
    format: [
      'Native video massively outperforms a link to video hosted elsewhere',
      'Square or vertical images; the feed is majority mobile',
      'Images: 1080 × 1350 (4:5) is the tallest the feed shows without cropping',
      'Keep copy short — Facebook truncates aggressively on mobile',
      'A question in the caption is the cheapest way to earn the comments that drive reach',
    ],
  },
  instagram: {
    headline: '1–7pm',
    bestDays: 'Tuesday through Friday, plus Saturday',
    bestTimes: '1:00–7:00pm — the team posts at 3pm',
    secondary: 'Late morning, 11am–1pm, and early evening until 9pm',
    avoid: 'Late night and very early morning',
    cadence: '3–4 feed posts per week, plus Stories whenever there is something to show.',
    sameDay:
      'One feed post a day is the ceiling; a second usually performs worse than the first ' +
      'would have alone. Stories are the exception and do not count — post those freely. If ' +
      'you do publish twice, leave a couple of hours between them — Monday\'s Quote Day and ' +
      'spotlight go at 1pm and 3pm.',
    why:
      'ChatGPT puts the Instagram window at 1–7pm, and that is what the team posts to: ' +
      'afternoon browsing through the end of the working day, when the feed is looked at ' +
      'rather than skimmed. This replaced the team\'s August research, which named three ' +
      'peaks (7–9am, 11am–1pm, 5–9pm); the late-morning and evening ends of that are kept ' +
      'as acceptable. It is still the only platform here where the weekend is worth posting on.',
    format: [
      'Every post needs an image or video — text-only posts are rejected outright',
      'Reels reach far beyond your followers; static posts mostly do not',
      'Images: 1080 × 1350 (4:5), up to 8 MB — the tallest Instagram accepts and the size the feed shows; square wastes screen on mobile',
      '9:16 is for Reels only — a 9:16 still image is refused as too tall',
      "ChatGPT's 1122 × 1402 is close but not exact — use Crop to 1080 × 1350 when you attach it",
      'First line of the caption is the only one shown — treat it as a headline',
      'Video is published as a Reel through this app; still images post to the feed',
    ],
  },
  twitter: {
    headline: 'Weekdays, 12–6pm',
    bestDays: 'Monday through Thursday are strongest; Friday morning still works',
    bestTimes: '12:00–6:00pm — the team posts at 12:30pm',
    secondary: 'Mornings, 9am–noon, when news and commentary circulate',
    avoid: 'Late evening and weekends — reach drops sharply',
    cadence: 'Daily is fine here, unlike anywhere else. The feed moves fast and posts have a short life.',
    sameDay:
      'The one network where several posts a day is normal. A post has a half-life measured ' +
      'in minutes, so two or three spaced a couple of hours apart reach largely different ' +
      'people rather than competing. Back-to-back within the same hour is still wasteful.',
    why:
      'ChatGPT puts the X window at noon to 6pm. X has the shortest half-life of any network ' +
      'here — a post is effectively finished within a couple of hours — so landing in an ' +
      'active window matters more than anywhere else. 12:30pm catches the lunch scroll and ' +
      'leaves the afternoon for replies.',
    format: [
      '280 characters is the hard ceiling — the strictest limit of any platform here',
      'Up to 4 images per post, or one video — not both',
      'Images: up to 5 MB — a larger one is dropped and the post goes out as text only; 1600 × 900 (16:9) shows uncropped everywhere, and 4:5 is fine too',
      'Threads work, but the first post has to stand alone',
      'Replying to others reaches further than posting does',
    ],
  },
  threads: {
    headline: 'Not connected yet',
    bestDays: '—',
    bestTimes: '—',
    secondary: '—',
    avoid: '—',
    cadence: '—',
    sameDay: '—',
    why:
      'No integration exists yet, so nothing can be scheduled here. Broadly, Threads behaves ' +
      'like a slower X — the same morning windows apply, but posts last longer.',
    format: ['500 character limit', 'Conversational rather than promotional copy performs better'],
  },
  bluesky: {
    headline: 'Not connected yet',
    bestDays: '—',
    bestTimes: '—',
    secondary: '—',
    avoid: '—',
    cadence: '—',
    sameDay: '—',
    why:
      'No integration exists yet. The audience skews technical and the feed is chronological ' +
      'rather than ranked, so posting time matters more literally than elsewhere.',
    format: ['300 character limit', 'Chronological feed — nothing resurfaces a post later'],
  },
};

/** Format '08:30' as '8:30am'. */
export const formatTime = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')}${period}`;
};

/** Every slot for a platform, in weekday order — used to count the weekly cadence. */
export const slotsFor = (platform: SocialPlatform): { day: Weekday; slot: Slot }[] =>
  WEEKDAYS.flatMap(d => {
    const slot = SCHEDULE[platform]?.[d.key];
    if (!slot) return [];
    // A day carrying two themes counts as two slots, or the cadence line under-reports
    return slot.also
      ? [{ day: d.key, slot }, { day: d.key, slot: slot.also }]
      : [{ day: d.key, slot }];
  });

// ---------------------------------------------------------------------------
// Machine-readable windows, for checking a chosen time against the guidance.
//
// The GUIDANCE strings above are prose for a human to read; these are the same intent in a
// form code can test. They are kept next to each other deliberately — if you change one,
// change the other, or the composer will warn about times the page recommends.
// ---------------------------------------------------------------------------

export interface Window {
  /** Days this window applies to. */
  days: Weekday[];
  /** Inclusive start and exclusive end, in 24-hour local hours. 8–10 means 08:00–09:59. */
  fromHour: number;
  toHour: number;
}

export const WINDOWS: Partial<Record<SocialPlatform, Window[]>> = {
  // ChatGPT's windows, which the team posts to (2026-10-10). Its table named a best Tuesday
  // window per network; it is applied to every day the schedule posts on, since a window that
  // excluded Monday or Friday would have the composer warn about times this file recommends.
  linkedin: [
    { days: ['mon', 'tue', 'wed', 'thu', 'fri'], fromHour: 11, toHour: 17 },
    // Secondary, so an earlier time sent deliberately is not flagged: the point is to catch a
    // 3am or Sunday post, not to insist on the single best slot.
    { days: ['mon', 'tue', 'wed', 'thu', 'fri'], fromHour: 9, toHour: 11 },
  ],
  facebook: [
    { days: ['mon', 'tue', 'wed', 'thu', 'fri'], fromHour: 12, toHour: 20 },
    { days: ['mon', 'tue', 'wed', 'thu', 'fri'], fromHour: 9, toHour: 12 },
  ],
  instagram: [
    { days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'], fromHour: 13, toHour: 19 },
    // The late-morning and evening ends of the team's August research stay acceptable
    { days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'], fromHour: 11, toHour: 13 },
    { days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'], fromHour: 19, toHour: 21 },
  ],
  twitter: [
    { days: ['mon', 'tue', 'wed', 'thu', 'fri'], fromHour: 12, toHour: 18 },
    { days: ['mon', 'tue', 'wed', 'thu', 'fri'], fromHour: 9, toHour: 12 },
  ],
};

const DAY_INDEX: Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** The weekday key for a Date, in the viewer's own timezone. */
export const weekdayOf = (date: Date): Weekday => DAY_INDEX[date.getDay()];

/** Does this moment fall inside any recommended window for the platform? */
export const isWithinWindow = (platform: SocialPlatform, date: Date): boolean => {
  const windows = WINDOWS[platform];
  // No windows defined (Threads, Bluesky) means nothing to advise, so never warn.
  if (!windows?.length) return true;
  const day = weekdayOf(date);
  const hour = date.getHours();
  return windows.some(w => w.days.includes(day) && hour >= w.fromHour && hour < w.toHour);
};

export interface TimeAdvice {
  platform: SocialPlatform;
  ok: boolean;
  /** Prose summary of when this platform prefers, for the warning line. */
  better: string;
}

/** Check a time against every selected platform. Empty when there is nothing to say. */
export const adviseOnTime = (platforms: SocialPlatform[], date: Date | null): TimeAdvice[] => {
  if (!date) return [];
  return platforms
    .filter(p => WINDOWS[p]?.length)
    .map(p => ({
      platform: p,
      ok: isWithinWindow(p, date),
      better: GUIDANCE[p]?.headline ?? '',
    }));
};

/**
 * The next few recommended moments, preferring times that suit the most selected platforms.
 *
 * Searches forward in hourly steps rather than solving it analytically: the search space is
 * one week, and this stays readable when the windows change.
 */
export const suggestTimes = (
  platforms: SocialPlatform[],
  from: Date = new Date(),
  count = 3
): { when: Date; suits: number }[] => {
  const relevant = platforms.filter(p => WINDOWS[p]?.length);
  if (!relevant.length) return [];

  // Start at the next whole hour, at least 15 minutes out so the time is not already passing
  const cursor = new Date(from.getTime() + 15 * 60000);
  cursor.setMinutes(0, 0, 0);
  cursor.setHours(cursor.getHours() + 1);

  // Score the whole week before choosing, rather than taking the first acceptable slots.
  // Walking forward and stopping early found Monday noon (one platform) and never reached
  // Tuesday noon, which suits several — so every suggestion scored 1 even when better
  // overlapping times existed later in the week.
  const candidates: { when: Date; suits: number }[] = [];
  for (let i = 0; i < 24 * 7; i++) {
    const when = new Date(cursor.getTime() + i * 3600000);
    const suits = relevant.filter(p => isWithinWindow(p, when)).length;
    if (suits > 0) candidates.push({ when, suits });
  }

  // Best coverage first, earliest among equals
  candidates.sort((a, b) => b.suits - a.suits || a.when.getTime() - b.when.getTime());

  // Thin out near-duplicates: consecutive hours inside one window would otherwise fill every
  // slot with 8am, 9am, 10am of the same morning.
  const results: { when: Date; suits: number }[] = [];
  for (const candidate of candidates) {
    if (results.length >= count) break;
    const tooClose = results.some(
      r => Math.abs(r.when.getTime() - candidate.when.getTime()) < 4 * 3600000
    );
    if (!tooClose) results.push(candidate);
  }

  // Present chronologically once chosen, so the buttons read as a timeline
  return results.sort((a, b) => a.when.getTime() - b.when.getTime());
};

// ---------------------------------------------------------------------------
// Theme lookup, for the guided composer.
//
// Derived from SCHEDULE rather than duplicated: a theme's platforms, days and times are
// already declared there, so asking "when does an employee spotlight go out" is a matter of
// reading the matrix in reverse. Add a theme by adding slots to SCHEDULE and it appears in
// the wizard automatically.
// ---------------------------------------------------------------------------

export interface ThemeSlot {
  platform: SocialPlatform;
  day: Weekday;
  /** 24-hour local, e.g. '09:00'. */
  time: string;
  occasional: boolean;
  note?: string;
}

/** Every theme in the schedule, with the slots that carry it. */
export const themeSlots = (): Map<string, ThemeSlot[]> => {
  const map = new Map<string, ThemeSlot[]>();
  (Object.keys(SCHEDULE) as SocialPlatform[]).forEach(platform => {
    WEEKDAYS.forEach(({ key: day }) => {
      const slot = SCHEDULE[platform]?.[day];
      if (!slot) return;
      // Both themes on a shared day are indexed, so either can be picked in the wizard
      [slot, slot.also].forEach(s2 => {
        if (!s2) return;
        const list = map.get(s2.theme) || [];
        list.push({
          platform,
          day,
          time: s2.time,
          occasional: Boolean(s2.occasional),
          note: s2.note,
        });
        map.set(s2.theme, list);
      });
    });
  });
  return map;
};

/** Theme names, ordered by the earliest weekday they run on, so the list reads as a week. */
export const themeNames = (): string[] => {
  const order = WEEKDAYS.map(d => d.key);
  return Array.from(themeSlots().entries())
    .sort((a, b) => {
      const first = (slots: ThemeSlot[]) => Math.min(...slots.map(s => order.indexOf(s.day)));
      return first(a[1]) - first(b[1]) || a[0].localeCompare(b[0]);
    })
    .map(([theme]) => theme);
};

/**
 * The next date a given weekday falls on, at a given time, strictly in the future.
 *
 * `from` defaults to now. If today is the right weekday but the time has passed, this returns
 * next week — scheduling something for 9am when it is already 10am would be refused by the
 * server anyway, and silently landing a week out is less surprising than an error.
 */
export const nextOccurrence = (day: Weekday, time: string, from: Date = new Date()): Date => {
  const order = WEEKDAYS.map(d => d.key);
  const [h, m] = time.split(':').map(Number);
  // WEEKDAYS starts at Monday; Date.getDay() starts at Sunday
  const targetDow = (order.indexOf(day) + 1) % 7;

  const result = new Date(from);
  result.setHours(h, m, 0, 0);
  let delta = (targetDow - result.getDay() + 7) % 7;
  if (delta === 0 && result <= from) delta = 7;
  result.setDate(result.getDate() + delta);
  return result;
};

// ---------------------------------------------------------------------------
// Best time per network for a chosen day, for the composer's "best time" switch.
//
// Ian asked 2026-10-19 for the day to be chosen once and each network's time filled in from
// this schedule, after a four-network post had been left at midnight and moved by hand.
// ---------------------------------------------------------------------------

/**
 * The time a network posts at most often across the week — what it gets on a day the schedule
 * leaves empty for it. Monday's second theme is not counted: it is the exception, not the slot.
 */
export const usualTime = (platform: SocialPlatform): string | null => {
  const counts = new Map<string, number>();
  WEEKDAYS.forEach(({ key }) => {
    const slot = SCHEDULE[platform]?.[key];
    if (slot) counts.set(slot.time, (counts.get(slot.time) || 0) + 1);
  });
  let best: string | null = null;
  counts.forEach((n, time) => {
    if (best === null || n > (counts.get(best) || 0)) best = time;
  });
  return best;
};

export interface BestTime {
  platform: SocialPlatform;
  when: Date;
  /** 24-hour local, e.g. '11:30'. */
  time: string;
  /** The schedule's theme for that day, or null when the network has no slot that day. */
  theme: string | null;
}

/**
 * Each network's time on the day `day` falls on, in the viewer's own timezone like the rest of
 * this file: that day's weekly slot when the schedule has one, otherwise the network's usual time,
 * otherwise noon for a network the schedule does not cover.
 */
export const bestTimesFor = (platforms: SocialPlatform[], day: Date): BestTime[] =>
  platforms.map(platform => {
    const listed = SCHEDULE[platform]?.[weekdayOf(day)];
    // The weekly post over an "as needed" one: Instagram's Monday lists the occasional
    // spotlight (3pm) first, but the post that goes out every Monday is Quote Day at 1pm.
    const slot = listed?.occasional && listed.also && !listed.also.occasional ? listed.also : listed;
    const time = slot?.time ?? usualTime(platform) ?? '12:00';
    const [h, m] = time.split(':').map(Number);
    const when = new Date(day);
    when.setHours(h, m, 0, 0);
    return { platform, when, time, theme: slot?.theme ?? null };
  });
